export const COLLECTIONS = [
  {
    id: "memory",
    title: "Saved memory",
    scope: "Your agent",
    color: "#4690d9",
    icon: "memory",
    description: "What your agent has saved about your business.",
  },
  {
    id: "preferences",
    title: "Team preferences",
    scope: "Your team",
    color: "#ec4899",
    icon: "team",
    description: "How your team likes to work, saved by your agent.",
  },
  {
    id: "learnings",
    title: "Learned playbooks",
    scope: "Your brand",
    color: "#16a34a",
    icon: "learning",
    description: "Client-specific lessons saved alongside your agent’s skills.",
  },
  {
    id: "skills",
    title: "Agent skills",
    scope: "Available to your agent",
    color: "#d97706",
    icon: "skill",
    description: "Skills discovered by your agent’s own runtime.",
  },
  {
    id: "brand",
    title: "Brand knowledge",
    scope: "Private workspace",
    color: "#7c3aed",
    icon: "brand",
    description: "Brand references, uploaded knowledge, and published files.",
  },
  {
    id: "shared",
    title: "Smart Marketer Brain",
    scope: "Shared knowledge",
    color: "#96bf48",
    icon: "shared",
    description:
      "Shared expertise connected to your agent through your workspace’s access.",
  },
];

export function scopedData(resource, slug, view) {
  if (
    resource.error ||
    resource.status === "error" ||
    resource.data?.clientSlug !== slug ||
    (view && resource.data?.view !== view)
  )
    return null;
  return resource.data;
}

export function buildCollections({
  runtime,
  brand,
  shared,
  files,
  uploads,
  connection,
}) {
  const runtimeEntries = runtime?.entries ?? [];
  const runtimeKinds = {
    memory: "memory",
    preferences: "preference",
    learnings: "learning",
    skills: "skill",
  };
  return COLLECTIONS.map((collection) => {
    let entries = [];
    if (runtimeKinds[collection.id])
      entries = runtimeEntries
        .filter((e) => e.kind === runtimeKinds[collection.id])
        .map((e) => ({
          ...e,
          id: `runtime:${e.id}`,
          recordId: e.id,
          origin: "runtime",
          scope: "Private workspace",
        }));
    if (collection.id === "brand" || collection.id === "shared") {
      const source = collection.id === "shared" ? shared : brand;
      entries = (source?.entries ?? []).map((e) => ({
        ...e,
        id: `${collection.id}:${e.id}`,
        recordId: e.id,
        origin: "publication",
        view: collection.id,
        kind: "knowledge",
        scope: collection.scope,
      }));
    }
    if (collection.id === "brand") {
      entries.push(
        ...(files?.files ?? []).map((f) => ({
          id: `file:${f.id}`,
          recordId: f.id,
          title: f.name,
          summary: `${f.source} · ${f.sizeBytes.toLocaleString()} bytes`,
          category: "Files",
          kind: "file",
          origin: "file",
          sourceLabel: f.source,
          updatedAt: f.updatedAt,
          scope: "Private workspace",
          file: f,
        })),
      );
      // Ready uploads already have a Brand Knowledge publication. Keep their
      // original attached to that record instead of counting the source twice.
      for (const upload of uploads?.uploads ?? []) {
        const published = entries.find(
          (e) =>
            e.recordId === upload.publicationId && e.origin === "publication",
        );
        if (published) published.upload = upload;
      }
    }
    return {
      ...collection,
      entries: entries.map((e) => ({
        ...e,
        collectionId: collection.id,
        color: collection.color,
      })),
      coverage: runtimeKinds[collection.id]
        ? (runtime?.coverage?.[collection.id] ?? "unavailable")
        : (collection.id === "brand" ? brand : shared)
          ? "available"
          : "unavailable",
      hasMore:
        collection.id === "brand"
          ? Boolean(brand?.hasMore || files?.hasMore)
          : collection.id === "shared"
            ? Boolean(shared?.hasMore)
            : false,
      connection: collection.id === "shared" ? connection : null,
    };
  });
}

export function childNodes(collection, group) {
  if (!collection) return [];
  const rows = group
    ? collection.entries.filter((e) =>
        group === "Files"
          ? e.kind === "file" || e.upload || e.category === "Files"
          : e.category === group,
      )
    : collection.entries;
  if (!group && rows.length > 8) {
    const groups = [...new Set(rows.map((e) => e.category))];
    if (groups.length > 1)
      return groups.map((title) => ({
        id: `group:${title}`,
        title,
        count: rows.filter((e) => e.category === title).length,
        kind: "group",
        category: title,
        color: collection.color,
      }));
  }
  return rows;
}

export function locateEntry(collections, id) {
  const collection = collections.find((c) =>
    c.entries.some((e) => e.id === id || e.recordId === id),
  );
  if (!collection) return null;
  const entry = collection.entries.find(
    (e) => e.id === id || e.recordId === id,
  );
  const grouped = childNodes(collection, null).some((e) => e.kind === "group");
  const group = grouped ? entry.category : null;
  const nodes = childNodes(collection, group);
  return {
    collection: collection.id,
    group,
    page: Math.floor(nodes.findIndex((e) => e.id === entry.id) / 8),
    selected: entry.id,
  };
}
